<script lang="typescript">
    import { state, setState } from "./stores.js";
    import { MessageType, FocalType } from "./types";
    import type { Lens } from "./types";
    import {
        computeTotalPrice,
        displayLensCodes,
        getAvailableBaseLensKeys,
        getBaseLensImage,
        getBaseLensName,
        getBaseLensObject,
        getBasePrice,
        getLensDetails,
        getLogo,
        getReadingAddString,
        getRxString,
    } from "./utils";
    import { fly } from "svelte/transition";
    import { onDestroy, onMount } from "svelte";
    import { lensTierNames, lensOption } from "./appData.js";
    import { push } from "svelte-spa-router";
    import LensCard from "./LensCard.svelte";
    import LensBenefitCard from "./LensBenefitCard.svelte";
    import FeaturedLensOptionsCard from "./FeaturedLensOptionsCard.svelte";

    if ($state.rxId === null) window.location.replace("/");

    onMount(() =>
        setState({
            message: MessageType.RESET_UPGRADE_LENS_BASE,
        })
    );

    const baseLensKeys = getAvailableBaseLensKeys($state.focalType, $state.readingAdd, $state.rxId);
    const lensObject = getBaseLensObject($state.focalType);
    const selectBaseLens = (lensTier: number): void => {
        // set the lens tier and base lens key, then go to the options page
        setState({
            message: MessageType.SET_BASE_LENS,
            lensTier,
            baseLensKey: baseLensKeys[lensTier],
        });
        push("/options/");
    };

    const upgadeBaseLens = (lensTier: number): void => {
        // set the lens tier and base lens key, then go to the upgrade page
        setState({
            message: MessageType.SET_UPGRADE_LENS_BASE,
            lensTier,
            upgradeLensKey: baseLensKeys[lensTier],
        });
        push("/upgrades/");
    };

    const hasUpgradeLenses = (option: Lens) => {
        return (
            Object.keys(option).includes("upgradeLensCodes") && option.upgradeLensCodes.length > 0
        );
    };

    // check if any of the baseLensKeys have upgradelenscodes
    const anyHaveUpgradeLenses = baseLensKeys.some((key) => {
        const option = lensObject[key];
        return hasUpgradeLenses(option);
    });

    const lensCardHeightClass = $state.focalType === FocalType.VARIFOCAL ? "h-60" : "h-40";

    var optionChecked = Array(baseLensKeys.length)
        .fill()
        .map(() => new Array());

    var prices = Array(baseLensKeys.length);
    Object.entries(getLensDetails(lensObject, baseLensKeys)).forEach(([key, value]) => {
        prices[key] = getBasePrice(value, $state.region);
    });

    const updatePrice = (lensTier: number, lensOption) => {
        // only allow one option per lens
        Object.entries(optionChecked[lensTier]).forEach(([key, value]) => {
            if (key !== lensOption) {
                optionChecked[lensTier][key] = false;
            }
        });
        // update the displayed price to include the selection option
        var variant = null;
        if (optionChecked[lensTier][lensOption]) {
            variant = lensOption;
        }
        prices[lensTier] = computeTotalPrice(
            lensObject,
            baseLensKeys[lensTier],
            variant,
            null,
            $state.region
        );
    };

    onDestroy(() => window.scrollTo(0, 0));
</script>

<section>
    {#if $state.focalType === FocalType.VARIFOCAL}
        <div class="w-full text-white shadow-inner bg-primary">
            <h3 class="py-2 mx-auto font-normal w-max">
                <span class="font-medium">Distance:</span>
                {getRxString($state.focalType, $state.rxId)},
                <span class="font-medium">Reading Add:</span>
                {getReadingAddString($state.readingAdd)}
            </h3>
        </div>
    {/if}

    <div class="page-container local-container">
        {#each getLensDetails(lensObject, baseLensKeys) as option, i}
            <div
                id="lens-column"
                class="flex flex-col items-center w-full pt-12 md:w-1/3 sm:min-h-screen"
                in:fly={{ y: 100, duration: 500, delay: i * 200 }}
            >
                <h1 class="text-4xl font-normal">{lensTierNames[i]}</h1>
                <LensCard
                    bind:price={prices[i]}
                    name={getBaseLensName(option, $state.region)}
                    image={getBaseLensImage(option, $state.region)}
                    baseHeightClass={lensCardHeightClass}
                />
                {#if displayLensCodes(option)}
                    <p>{displayLensCodes(option)}</p>
                {/if}

                {#if anyHaveUpgradeLenses}
                    <div class="h-12">
                        {#if hasUpgradeLenses(option) && i > 0}
                            <button
                                type="button"
                                class="btn-upgrade"
                                on:click={() => upgadeBaseLens(i)}
                            >
                                Upgrade this lens design
                            </button>
                        {/if}
                    </div>
                {/if}

                {#if typeof option.name === "string"}
                    <div class="w-full px-6 sm:h-52 md:h-40 lg:h-24">
                        {option.description}
                    </div>
                {/if}

                <!-- options -->
                <div class="flex flex-col items-stretch w-full py-6">
                    {#each option.benefits as benefit}
                        <LensBenefitCard {...benefit} />
                    {/each}
                </div>

                <!-- featured options -->
                {#each option.variants as variant, v}
                    {#if variant.optionType === lensOption.TRANSITIONS || variant.optionType === lensOption.TRANSITIONS_XTRA}
                        <FeaturedLensOptionsCard
                            {variant}
                            logo={getLogo(variant.optionType)}
                            lensTier={i}
                            baseLensKey={baseLensKeys[i]}
                            primaryVariantIndex={v}
                            bind:checked={optionChecked[i][variant.optionType]}
                            onToggle={() => updatePrice(i, variant.optionType)}
                        />
                    {/if}
                {/each}

                <button
                    type="button"
                    class="w-2/3 px-4 py-2 mt-auto mb-12 text-lg btn-primary"
                    on:click={() => selectBaseLens(i)}
                >
                    Discover additional lens options
                </button>
            </div>
        {/each}
    </div>
</section>

<style>
    #lens-column:nth-child(2) {
        @apply bg-primary-fade;
    }

    .local-container {
        @apply flex-row justify-center flex-wrap sm:flex-nowrap;
        @apply items-stretch overflow-y-hidden;
    }
</style>
